import React, { JSX, useCallback, useEffect, useMemo, useState } from "react";
import { ActiveStatus } from "models/enumerations/active-status/active-status";
import { Banner, BannerStyle } from "components/banner/banner";
import { Breadcrumbs } from "components/breadcrumbs/breadcrumbs";
import { Button, ButtonStyle } from "components/buttons/button/button";
import { ButtonIcon } from "components/buttons/button-icon/button-icon";
import { CollectionUtils } from "andculturecode-javascript-core";
import { CourseDetailsChangeHistory } from "components/courses/course-details/course-details-change-history/course-details-change-history";
import { CourseDetailsChangesModal } from "components/courses/course-details/course-details-changes-modal/course-details-changes-modal";
import { CourseDetailsForm } from "components/courses/course-details/course-details-form/course-details-form";
import { CourseRecord } from "models/view-models/courses/course-record";
import { EditCourseLayout } from "layouts/admin/edit-course-layout/edit-course-layout";
import { HeaderActions } from "components/header-actions/header-actions";
import { Heading, HeadingPriority, HeadingSize } from "components/typography/heading/heading";
import { Icons } from "components/icons/constants/icons";
import { Modal, ModalAction } from "components/modal/modal";
import { NavigationRequest } from "utilities/hooks/navigation/use-navigation-requests";
import { NotificationBanner } from "components/notification-banner/notification-banner";
import { NotificationType } from "models/enumerations/notifications/notification-type";
import {
    Paragraph,
    ParagraphSize,
    ParagraphStyle,
} from "components/typography/paragraph/paragraph";
import { ReadOnlyContext } from "utilities/contexts/use-read-only-context";
import { sitemap } from "sitemap";
import { t } from "utilities/localization/t";
import { useCourse } from "utilities/contexts/use-course-context";
import { useCourseDetailsArchive } from "utilities/hooks/models/courses/use-course-detail-archives";
import {
    useListenForNavigationRequests,
    useResolveNavigationRequest,
} from "utilities/contexts/navigation/use-navigation-request-context";
import { useRedirectOnForbidden } from "utilities/hooks/aspects/authorization/use-redirect-on-forbidden";
import "./edit-course-details-page.scss";
import { validatePageAccess } from "utilities/decorators/aspects/authorization/validate-page-access";
import { AccessControlKeys } from "utilities/enumerations/authorization/access-control-keys";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface EditCourseDetailsPageProps {}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const CSS_CLASS_NAME: string = "edit-course-details-page";

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const EditCourseDetailsPage: React.FC<EditCourseDetailsPageProps> = validatePageAccess(
    AccessControlKeys.EditCourseDetailsPage
)((): JSX.Element => {
    const { record: course, setRecord: setCourse } = useCourse();
    const [courseDetailsWithEdits, setCourseDetailsWithEdits] = useState<CourseRecord>(course);
    const [showEditModeModal, setShowEditModeModal] = useState<boolean>(false);
    const [showSaveDetailsChangesModal, setShowSaveDetailsChangesModal] = useState<boolean>(false);
    const [showCancelChangesModal, setShowCancelChangesModal] = useState<boolean>(false);
    const [editMode, setEditMode] = useState<boolean>(false);
    const activeCourse = !editMode && course.activatedOn != null;
    const [readOnly, setReadOnly] = useState<boolean>(false);

    useEffect(() => {
        setCourseDetailsWithEdits(course);
    }, [course]);

    useRedirectOnForbidden(sitemap.admin.course.list);
    const [dirty, setDirty] = useState<boolean>(false);
    const [navigationRequest, setNavigationRequest] = useState<NavigationRequest>();
    const showCancelEditOnNavigationModal = useMemo(
        (): boolean => navigationRequest != null,
        [navigationRequest]
    );
    const navigationRequests = useListenForNavigationRequests(EditCourseDetailsPage.name, [
        Breadcrumbs.name,
        EditCourseLayout.name,
    ]);
    const resolveNavigationRequest = useResolveNavigationRequest();

    useEffect(() => {
        if (CollectionUtils.isEmpty(navigationRequests)) {
            return;
        }

        const navigationRequest = navigationRequests[0];

        if (!editMode || !dirty) {
            resolveNavigationRequest(
                EditCourseDetailsPage.name,
                navigationRequest.requestingComponent
            );
            navigationRequest.onNavigationApproved();
            return;
        }
        setNavigationRequest(navigationRequest);
    }, [editMode, dirty, navigationRequests, resolveNavigationRequest]);

    const handleNavigationApproved = useCallback((): void => {
        setNavigationRequest(undefined);

        if (navigationRequest?.onNavigationApproved != null) {
            resolveNavigationRequest(
                EditCourseDetailsPage.name,
                navigationRequest.requestingComponent
            );
            navigationRequest.onNavigationApproved();
        }
    }, [navigationRequest, resolveNavigationRequest]);

    const handleNavigationDenied = useCallback((): void => {
        setNavigationRequest(undefined);

        if (navigationRequest == null) {
            return;
        }

        resolveNavigationRequest(EditCourseDetailsPage.name, navigationRequest.requestingComponent);
    }, [navigationRequest, resolveNavigationRequest]);

    const cancelEditModeOnNavigationConfirmationActions: ModalAction[] = [
        {
            buttonText: t("noKeepEditing"),
            dataTestId: "edit-course-details-page:keep-editing:btn",
            onClick: handleNavigationDenied,
            style: ButtonStyle.Secondary,
        },
        {
            buttonText: t("yesLeave"),
            dataTestId: "edit-course-details-page:yes-leave:btn",
            onClick: handleNavigationApproved,
            style: ButtonStyle.Destructive,
        },
    ];

    useEffect(() => {
        if (course.status === ActiveStatus.Active && !editMode) {
            setReadOnly(true);
        }

        if (course.status === ActiveStatus.Inactive || course.status === ActiveStatus.Archived) {
            setReadOnly(true);
        }
        if (editMode) {
            setReadOnly(false);
        }
    }, [course.status, editMode]);

    const handleCourseDetailsChange = (course: CourseRecord): void => {
        if (editMode) {
            setCourseDetailsWithEdits(course);
        } else {
            setCourse(course);
        }
    };

    const enterEditModalActionArray: ModalAction[] = [
        {
            buttonText: t("cancel"),
            dataTestId: "edit-course-details-page:modal-cancel:btn",
            onClick: () => setShowEditModeModal(false),
            style: ButtonStyle.Secondary,
        },
        {
            buttonText: t("continueToEdit"),
            dataTestId: "edit-course-details-page:modal-edit:btn",
            onClick: () => onEnterEditModalClose(),
            style: ButtonStyle.Primary,
        },
    ];

    const onEnterEditModalClose = () => {
        setShowEditModeModal(false);
        setEditMode(true);
    };

    const cancelEditModalActionArray: ModalAction[] = [
        {
            buttonText: t("noKeepEditing"),
            dataTestId: "edit-course-details-page:modal-keep-editing:btn",
            onClick: () => setShowCancelChangesModal(false),
            style: ButtonStyle.Transparent,
        },
        {
            buttonText: t("yesCancel"),
            dataTestId: "edit-course-details-page:modal-cancel:btn",
            onClick: () => onCancelEditModalClose(),
            style: ButtonStyle.Secondary,
        },
    ];

    const onCancelEditModalClose = () => {
        setCourseDetailsWithEdits(course);
        setShowCancelChangesModal(false);
        setEditMode(false);
    };

    var { courseDetailsArchive, setRefresh } = useCourseDetailsArchive({
        courseId: course.id,
        includeCreatedBy: true,
        includeLastModifiedBy: true,
    });

    useEffect(() => {
        setRefresh(true);
    }, [course.status, setRefresh]);

    return (
        <>
            {course.status === ActiveStatus.Archived && (
                <Banner
                    cssClassName={`${CSS_CLASS_NAME}__archived-banner`}
                    style={BannerStyle.Dark}>
                    {t("youAreViewingAnArchivedCourse")}
                </Banner>
            )}
            <div className={CSS_CLASS_NAME}>
                <div className={`${CSS_CLASS_NAME}__header`}>
                    <HeaderActions
                        dataTestId={"edit-course-details-page:header-actions:div"}
                        title={t("courseDetails")}
                        headingPriority={HeadingPriority.H1}
                        headingSize={HeadingSize.Small}>
                        {activeCourse && (
                            <Button
                                disabled={
                                    course.status === ActiveStatus.Archived ||
                                    course.status === ActiveStatus.Inactive
                                }
                                dataTestId="edit-course-details-page:edit-course:btn"
                                onClick={() => setShowEditModeModal(true)}
                                style={ButtonStyle.Primary}
                                text={t("editCourseDetails")}
                            />
                        )}
                        {editMode && (
                            <div className={`${CSS_CLASS_NAME}__edit-mode-buttons`}>
                                <Button
                                    dataTestId="edit-course-details-page:cancel-edit-course:btn"
                                    onClick={() => setShowCancelChangesModal(true)}
                                    style={ButtonStyle.Secondary}
                                    text={t("cancelEdit")}
                                />
                                <ButtonIcon
                                    buttonStyle={ButtonStyle.Primary}
                                    dataTestId="edit-course-details-page:save-changes:btn"
                                    iconType={Icons.PublishWithChanges}
                                    onClick={() => setShowSaveDetailsChangesModal(true)}
                                    text={t("saveChanges")}
                                />
                            </div>
                        )}
                    </HeaderActions>
                </div>
                {editMode && (
                    <div className={`${CSS_CLASS_NAME}__banner`}>
                        <NotificationBanner
                            notificationId={0}
                            onClose={() => {}}
                            style={NotificationType.Default}
                            message={t(
                                "changesWillBeAppliedToAllLearnersRegardlessOfVersionOrCompletionStatusAndWillNotImpactOrResetALearnersProgress"
                            )}
                        />
                    </div>
                )}
                <ReadOnlyContext.Provider value={{ readOnly, setReadOnly: () => setReadOnly }}>
                    <div className={`${CSS_CLASS_NAME}__content`}>
                        <CourseDetailsForm
                            course={editMode ? courseDetailsWithEdits : course}
                            editMode={editMode}
                            onCourseDetailsChange={handleCourseDetailsChange}
                            setCourse={setCourse}
                            setDirty={setDirty}
                        />
                        {course.activatedOn != null && (
                            <>
                                <div className={`${CSS_CLASS_NAME}__divider`}></div>
                                <CourseDetailsChangeHistory
                                    courseDetailsArchiveData={courseDetailsArchive}
                                />
                            </>
                        )}
                    </div>
                </ReadOnlyContext.Provider>
                <Modal
                    actions={enterEditModalActionArray}
                    isOpen={showEditModeModal}
                    onModalClose={() => setShowEditModeModal(false)}
                    title={t("editingMode")}>
                    <div>
                        <Heading
                            cssClassName={`${CSS_CLASS_NAME}__modal-heading`}
                            priority={HeadingPriority.H5}
                            size={HeadingSize.XSmall}>
                            {t("youAreGoingToEditTheCourseDetails")}
                        </Heading>
                        <Paragraph size={ParagraphSize.Large}>
                            {t("howChangesAreApplied")}
                        </Paragraph>
                        <Paragraph size={ParagraphSize.Medium} style={ParagraphStyle.Light}>
                            {t(
                                "changesWillBeAppliedToAllLearnersRegardlessOfVersionOrCompletionStatusAndWillNotImpactOrRestALearnersProgress"
                            )}
                        </Paragraph>
                    </div>
                </Modal>
                <Modal
                    actions={cancelEditModalActionArray}
                    cssClassName={`${CSS_CLASS_NAME}__cancel-edit`}
                    isOpen={showCancelChangesModal}
                    modalStyle={"-inverted"}
                    onModalClose={() => {}}>
                    {t("AreYouSureYouWouldLikeToCancelWithoutSavingYourChanges")}
                </Modal>
                <CourseDetailsChangesModal
                    courseDetailsWithEdits={courseDetailsWithEdits}
                    setEditMode={setEditMode}
                    setRefresh={setRefresh}
                    setShowSaveDetailsChangesModal={setShowSaveDetailsChangesModal}
                    showSaveDetailsChangesModal={showSaveDetailsChangesModal}
                />
                <Modal
                    cssClassName={`${CSS_CLASS_NAME}__cancel-edit-mode-modal`}
                    isOpen={showCancelEditOnNavigationModal}
                    onModalClose={() => {}}
                    actions={cancelEditModeOnNavigationConfirmationActions}
                    modalStyle={"-inverted"}>
                    {t("areYouSureYouWouldLikeToLeaveWithoutSavingYourChanges")}
                </Modal>
            </div>
        </>
    );
});

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { EditCourseDetailsPage };

// #endregion Exports
